import {
  CompanyGroupingName,
  FactAction,
  FactActionType,
  FactPropertyForCategory,
  FactPropertyTypes,
  FactType,
  RawFactProperties
} from '../interfaces/fact';

export const FACT_FILTER_ON = 'entityUris';

// FactAction buttons that can be shown next to a FactSummary
export const DEFAULT_INLINE_ACTIONS: Array<FactAction> = [
  {
    type: FactActionType.OpenInStack,
    icon: 'icon2-x16-stack'
  },
  {
    type: FactActionType.AddToFilter,
    icon: 'icon2-x16-filter'
  },
  {
    type: FactActionType.SeeSignals,
    icon: 'icon2-x16-signal'
  }
];

// FactAction buttons that can be shown in the Fact Stack
export const DEFAULT_MAIN_STACK_ACTIONS: Array<FactAction> = [
  {
    type: FactActionType.SeeSignals,
    icon: 'icon2-x16-signal'
  },
  {
    type: FactActionType.AddToFilter,
    icon: 'icon2-x16-filter'
  }
  // We are commenting this as we have a button now for create element.
  // {
  //   type: FactActionType.CreateElement,
  //   icon: 'icon2-x16-plus-m'
  // }
];

// FactAction buttons for the context menu
export const DEFAULT_CONTEXT_MENU_ACTIONS: Array<FactAction> = [
  {
    type: FactActionType.CreateElement,
    icon: null
  },
  {
    type: FactActionType.AddToFilter,
    icon: null
  },
  {
    type: FactActionType.SeeSignals,
    icon: null
  },
  {
    type: FactActionType.CopyUrl,
    icon: null
  }
];

export const TOPICS_FILTER_ON = 'topic';

// NOTE: The Person/Place/Organization flags should generally be enabled
// for backwards compatibility with existing presets/bookmarks
export const FACTS_FEATURE_FLAGS = {
  FACTS: 'insights.app.facts',
  STACK: 'insights.app.facts-stack',
  CASUAL_SEARCH: 'insights.app.facts-casual-search',
  [FactType.Corporation]: 'insights.app.facts-companies',
  [FactType.Industry]: 'insights.app.facts-industries',
  [FactType.Organization]: 'insights.app.facts-organizations',
  [FactType.Person]: 'insights.app.facts-people',
  [FactType.Place]: 'insights.app.facts-places',
  [FactType.Product]: 'insights.app.facts-products',
  [FactType.StartUp]: 'insights.app.facts-startups',
  [FactType.Technology]: 'insights.app.facts-technologies',
  [FactType.Topic]: null
};

// Strings to be used for stacked views
export enum StackTemplate {
  ENTITY = 'e',
  DOCUMENT = 'd',
  FACT = 'f',
  CLUSTER = 'c'
}

/**
 * Fetch the properties that we want to show in the collapsible for the given category
 * Properties are ordered as we want them to be shown (hideIfEmpty properties come last)
 *
 * Reference: https://itonics.atlassian.net/wiki/spaces/PRODUCT/pages/1607083782278905857/FRD+CO-190+Working+with+Entities+I#Fields
 *
 * @param category Returns the property list for this category
 *
 * @returns Array of properties to be shown for this category (ordered)
 */
export const factPropertiesForCategory = (category: FactType): Array<FactPropertyForCategory> => {
  switch (category) {
    // Startups, Companies and Organizations share the same properties
    case FactType.StartUp:
    case FactType.Corporation:
    case FactType.Organization:
      // We only group company fact data for now.
      return [
        { property: RawFactProperties.addressCountry, hide: true },
        { property: RawFactProperties.industry, hideIfEmpty: true, hide: true },
        { property: RawFactProperties.facetIds, hideIfEmpty: true, valueType: FactPropertyTypes.PLAIN_TEXT, hide: true },
        { property: RawFactProperties.longDescription, hideIfEmpty: true, valueType: FactPropertyTypes.PLAIN_TEXT, hide: true },
        { property: RawFactProperties.categories, hideIfEmpty: true, valueType: FactPropertyTypes.PLAIN_TEXT, hide: true },
        { property: RawFactProperties.industry, hideIfEmpty: true, valueType: FactPropertyTypes.PLAIN_TEXT, hide: true },
        { property: RawFactProperties.legalName, hideIfEmpty: true, valueType: FactPropertyTypes.PLAIN_TEXT, hide: true },
        { property: RawFactProperties.headerImageUrl, hide: true, valueType: FactPropertyTypes.PLAIN_TEXT },
        {
          property: RawFactProperties.types,
          hideIfEmpty: true,
          hide: true,
          valueType: FactPropertyTypes.TYPE,
          groupName: CompanyGroupingName.company
        },
        {
          property: RawFactProperties.companyType,
          hideIfEmpty: true,
          hide: true,
          valueType: FactPropertyTypes.ORG_TYPE,
          groupName: CompanyGroupingName.company
        },
        {
          property: RawFactProperties.foundedOn,
          hideIfEmpty: true,
          valueType: FactPropertyTypes.DATE,
          groupName: CompanyGroupingName.company
        },
        {
          property: RawFactProperties.closedOn,
          hideIfEmpty: true,
          hide: true,
          valueType: FactPropertyTypes.DATE,
          groupName: CompanyGroupingName.company
        },
        {
          property: RawFactProperties.founders,
          hideIfEmpty: true,
          hide: true,
          valueType: FactPropertyTypes.PLAIN_TEXT,
          groupName: CompanyGroupingName.company
        },
        {
          property: RawFactProperties.location,
          hideIfEmpty: true,
          hide: true,
          valueType: FactPropertyTypes.PLAIN_TEXT,
          groupName: CompanyGroupingName.company
        },
        {
          property: RawFactProperties.numEmployees,
          hideIfEmpty: true,
          valueType: FactPropertyTypes.PLAIN_TEXT,
          groupName: CompanyGroupingName.company
        },
        {
          property: RawFactProperties.netProfit,
          hideIfEmpty: true,
          valueType: FactPropertyTypes.PLAIN_TEXT,
          groupName: CompanyGroupingName.company
        },
        {
          property: RawFactProperties.ipoStatus,
          hideIfEmpty: true,
          valueType: FactPropertyTypes.IPO_STATUS,
          groupName: CompanyGroupingName.company
        },
        { property: RawFactProperties.headquarters, hideIfEmpty: true, hide: true, groupName: CompanyGroupingName.company },
        {
          property: RawFactProperties.numFounders,
          hideIfEmpty: true,
          hide: true,
          valueType: FactPropertyTypes.INTEGER,
          groupName: CompanyGroupingName.company
        },
        {
          property: RawFactProperties.status,
          hideIfEmpty: true,
          hide: true,
          valueType: FactPropertyTypes.ORG_STATUS,
          groupName: CompanyGroupingName.company
        },
        {
          property: RawFactProperties.childOrgs,
          hideIfEmpty: true,
          hide: true,
          valueType: FactPropertyTypes.LIST,
          groupName: CompanyGroupingName.company
        },
        {
          property: RawFactProperties.contactEmail,
          hideIfEmpty: true,
          hide: true,
          valueType: FactPropertyTypes.PLAIN_TEXT,
          groupName: CompanyGroupingName.contact
        },
        {
          property: RawFactProperties.linkedin,
          hideIfEmpty: true,
          valueType: FactPropertyTypes.SOCIAL,
          groupName: CompanyGroupingName.contact
        },
        {
          property: RawFactProperties.twitter,
          hideIfEmpty: true,
          valueType: FactPropertyTypes.SOCIAL,
          groupName: CompanyGroupingName.contact
        },
        {
          property: RawFactProperties.facebook,
          hideIfEmpty: true,
          valueType: FactPropertyTypes.SOCIAL,
          groupName: CompanyGroupingName.contact
        },
        {
          property: RawFactProperties.websiteUrl,
          hideIfEmpty: true,
          valueType: FactPropertyTypes.LINK,
          groupName: CompanyGroupingName.contact
        },
        {
          property: RawFactProperties.phoneNumber,
          hideIfEmpty: true,
          valueType: FactPropertyTypes.PLAIN_TEXT,
          groupName: CompanyGroupingName.contact
        }
      ];

    case FactType.Place:
      return [
        { property: RawFactProperties.addressCountry },
        { property: RawFactProperties.containedInPlace },
        { property: RawFactProperties.dateCreated }
      ];

    case FactType.Product:
      return [
        { property: RawFactProperties.manufacturer },
        { property: RawFactProperties.brand },
        { property: RawFactProperties.dateCreated }
      ];

    case FactType.Person:
      return [
        { property: RawFactProperties.facetIds, hideIfEmpty: true, valueType: FactPropertyTypes.PLAIN_TEXT },
        { property: RawFactProperties.currency, hideIfEmpty: true, valueType: FactPropertyTypes.CURRENCY },
        { property: RawFactProperties.value, hideIfEmpty: true, valueType: FactPropertyTypes.CURRENCY },
        { property: RawFactProperties.facetIds, hideIfEmpty: true, valueType: FactPropertyTypes.PLAIN_TEXT },
        { property: RawFactProperties.bornOn, hideIfEmpty: true, valueType: FactPropertyTypes.DATE },
        { property: RawFactProperties.linkedin, hideIfEmpty: true, valueType: FactPropertyTypes.SOCIAL },
        { property: RawFactProperties.facebook, hideIfEmpty: true, valueType: FactPropertyTypes.SOCIAL },
        { property: RawFactProperties.twitter, hideIfEmpty: true, valueType: FactPropertyTypes.SOCIAL },
        { property: RawFactProperties.diedOn, hideIfEmpty: true, valueType: FactPropertyTypes.DATE },
        { property: RawFactProperties.gender, hideIfEmpty: true, valueType: FactPropertyTypes.PLAIN_TEXT },
        { property: RawFactProperties.locationIdentifiers, hideIfEmpty: true, valueType: FactPropertyTypes.PLAIN_TEXT },
        { property: RawFactProperties.locationGrowUp, hideIfEmpty: true, valueType: FactPropertyTypes.PLAIN_TEXT },
        { property: RawFactProperties.numCurrentJobs, hide: true, valueType: FactPropertyTypes.INTEGER },
        { property: RawFactProperties.numJobs, hide: true, valueType: FactPropertyTypes.INTEGER },
        { property: RawFactProperties.primaryOrg, hideIfEmpty: true, valueType: FactPropertyTypes.PLAIN_TEXT },
        { property: RawFactProperties.foundedOrgs, hide: true, valueType: FactPropertyTypes.PLAIN_TEXT },
        { property: RawFactProperties.jobs, hideIfEmpty: true, valueType: FactPropertyTypes.INTEGER },
        { property: RawFactProperties.primaryJobTitle, hideIfEmpty: true, valueType: FactPropertyTypes.PLAIN_TEXT },
        { property: RawFactProperties.websiteUrl, hideIfEmpty: true, valueType: FactPropertyTypes.LINK },
        { property: RawFactProperties.headerImageUrl, hide: true, valueType: FactPropertyTypes.PLAIN_TEXT },
        { property: RawFactProperties.permalink, hide: true, valueType: FactPropertyTypes.PLAIN_TEXT }
      ];

    // For all other types, don't show the collapsible
    default:
      return null;
  }
};

// Mapping between Fact Types and their corresponding Filters in insights
//   filterName: id for the filter
//   on: for the LeafFilter
//   useLabel: true  -> use fact's label as the filter value
//             false -> use fact's uri as the filter value
export const FACT_FILTERS_MAP: { [key: string]: { filterName: string; on: string; useLabel?: boolean } } = {
  [FactType.Corporation]: {
    filterName: 'companies',
    on: FACT_FILTER_ON
  },
  [FactType.Industry]: {
    filterName: 'industries',
    on: FACT_FILTER_ON
  },
  [FactType.Organization]: {
    filterName: 'organization',
    on: 'organization',
    useLabel: true
  },
  [FactType.Person]: {
    filterName: 'person',
    on: 'person',
    useLabel: true
  },
  [FactType.Place]: {
    filterName: 'place',
    on: 'place',
    useLabel: true
  },
  [FactType.Product]: {
    filterName: 'products',
    on: FACT_FILTER_ON
  },
  [FactType.StartUp]: {
    filterName: 'startUps',
    on: FACT_FILTER_ON
  },
  [FactType.Technology]: {
    filterName: 'technologies',
    on: FACT_FILTER_ON
  },
  [FactType.Topic]: {
    filterName: 'topic',
    on: 'topic',
    useLabel: true
  }
};

// Default language for title-casing
export const DEFAULT_CASE_LANGUAGE = 'en';
